import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import { IoMdArrowForward } from 'react-icons/io'
import { DetailList, FormattedCurrency, LoadingSpinner } from '@ideon-api/ideon-ui'

import {
  isNil,
  isEqual,
  isUndefined,
  pick,
  isEmpty,
  startCase,
  omitBy,
  capitalize,
  sortBy,
  uniqBy,
  reverse
} from 'lodash'

import quoteRateMemberRatesActions from '../../../actions/VericredQuoteRateMemberRates'

const HEADER_CLASS = 'text-sm font-semibold mt-4 mb-1'
const GROUP_CLASS = 'mb-3'

export class PlanRatesComponent extends Component {
  static propTypes = {
    census: PropTypes.object,
    planPremiumsByFamily: PropTypes.array.isRequired,
    plan: PropTypes.object.isRequired,
    quoteRateMemberRates: PropTypes.object,
    quoteRateMemberRatesCreate: PropTypes.func.isRequired,
    quoteRateMemberRatesReset: PropTypes.func.isRequired,
    quoteRateMemberRatesCreated: PropTypes.bool,
    quoteRates: PropTypes.array
  }

  componentDidMount() {
    const { plan, planPremiumsByFamily, quoteRateMemberRates, quoteRateMemberRatesCreated } = this.props
    if (planPremiumsByFamily[0].rate_id) {
      if ((!quoteRateMemberRatesCreated
        && !quoteRateMemberRates.requestInProgress
        && !quoteRateMemberRates.requestComplete)
        || (planPremiumsByFamily[0].rate_id) !== quoteRateMemberRates.attributes.rate_id) {
        this.props.quoteRateMemberRatesReset()
        this.props.quoteRateMemberRatesCreate(
          {
            rate_id: planPremiumsByFamily[0].rate_id,
            plan_id: plan.id
          }
        )
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.planPremiumsByFamily[0].rate_id) {
      if (!isNil(nextProps.quoteRateMemberRates)
        && (nextProps.quoteRateMemberRates.created === true
          && nextProps.quoteRateMemberRates.attributes.plan_id === this.props.plan.id)) {
        return true
      }
      return false
    }

    return !isEqual(pick(nextProps, 'plan'), pick(this.props, 'plan'))
  }

  componentWillUnmount() {
    this.props.quoteRateMemberRatesReset()
  }

  get familyComponents() {
    const { planPremiumsByFamily } = this.props

    return (
      <DetailList.Root>
        {planPremiumsByFamily.map((fp) => (
          <DetailList.Item
            key={`${fp.name}-${fp.premium}`}
            label={this.formatKey(fp.name)}
            value={this.familyPremiumValue(fp)}
            data-testid={`rate-family-${fp.name}`}
          />
        ))}
      </DetailList.Root>
    )
  }

  get quoteRateMemberRatesComponents() {
    if (['dental', 'vision', 'accident'].includes(this.props.plan.product_line)) {
      return this.isAgeBanded ? this.ratesByFamily : this.ratesByTier
    }
    return this.ratesByFamily
  }

  get ratesByFamily() {
    const { quoteRateMemberRates, plan } = this.props

    return quoteRateMemberRates.responseAttributes.map((familyPlan, index) => (
      <div key={`${plan.id}-famiy-rates-${index}`} className={GROUP_CLASS}>
        <div className={HEADER_CLASS} data-testid="rate-header">{startCase(familyPlan.member_external_id)}</div>

        {this.buildFamilyPremiums(familyPlan)}
      </div>
    ))
  }

  get ratesByTier() {
    const rates = this.props.quoteRateMemberRates.responseAttributes
    const tiers = rates.map((familyPlan, index) => omitBy(familyPlan.premiums, isEmpty))
    const groupedTiers = tiers.reduce((obj, tier) => {
      // eslint-disable-next-line no-return-assign, no-param-reassign
      Object.keys(tier).forEach((key, index) => (obj[key] || (obj[key] = [])).push(Object.values(tier)[index]))
      return obj
    }, {})

    return sortBy(Object.keys(groupedTiers), (key) => { return this.tierRank[key] }).map((key, index) => (
      <div key={`${this.props.plan.id}-tier-rates-${index}`} className={GROUP_CLASS}>
        <div className={HEADER_CLASS} data-testid="rate-header">{startCase(key)}</div>

        <DetailList.Root>
          {sortBy(uniqBy(groupedTiers[key], 'category'), (tier) => {
            return this.categoryRank[tier.category]
          }).map((tier, i) => (
            <DetailList.Item
              key={`${this.props.plan.id}-tier-category-rates-${i}`}
              label={startCase(tier.category)}
              value={<FormattedCurrency value={this.toCurrency(tier.total)} />}
              data-testid={`rate-tier-category-${this.props.plan.id}-${key}-${i}`}
            />
          ))}
        </DetailList.Root>
      </div>
    ))
  }

  get tierRank() {
    return {
      '4_tier_composite': 1,
      '3_tier_composite': 2,
      '2_tier_composite': 3,
      adult_child_tier_composite: 4,
      age_banded: 5
    }
  }

  get categoryRank() {
    return {
      employee_only: 1,
      employee_plus_spouse: 2,
      employee_plus_children: 3,
      employee_plus_family: 4
    }
  }

  get isAgeBanded() {
    const rates = this.props.quoteRateMemberRates.responseAttributes
    return rates.some((rate) => rate.premiums && rate.premiums.age_banded && !isEmpty(rate.premiums.age_banded))
  }

  toCurrency = (value) => {
    if (isNil(value) || value === '') return null

    const number = Number(value)
    return Number.isFinite(number) ? number : null
  }

  familyPremiumValue = (fp) => {
    if (fp.premium === 0 || isNil(fp.premium)) return ''

    return <FormattedCurrency value={this.toCurrency(fp.premium)} />
  }

  buildFamilyPremiums = (familyPlan) => {
    const premiums = omitBy(familyPlan.premiums, isEmpty)
    const familyPremiums = Object.keys(premiums).map((key) => ({ key, value: premiums[key] }))

    return familyPremiums.map((premium, index) => (
      premium.key === 'age_banded' ? this.ageBanded(premium, familyPlan) : this.compositeTier(premium, familyPlan)
    ))
  }

  compositeTier = (premium, familyPlan) => {
    return (
      <DetailList.Root key={`${familyPlan.id}-tier-${premium.key}`}>
        <DetailList.Item
          label={startCase(premium.key)}
          value={<FormattedCurrency value={this.toCurrency(premium.value.total)} />}
          data-testid={`rate-composite-tier-${familyPlan.id}-${premium.key}`}
        />
        <DetailList.Item
          label={startCase(premium.value.category)}
          value={<FormattedCurrency value={this.toCurrency(premium.value.total)} />}
          data-testid={`rate-composite-category-${familyPlan.id}-${premium.key}`}
        />
      </DetailList.Root>
    )
  }

  ageBanded = (premium, familyPlan) => {
    const dependentPremiums = reverse([...(premium.value.dependent_premiums || [])])
    const premiumValues = [premium.value.member]
    dependentPremiums.forEach((m) => premiumValues.push(m.premium))

    return (
      <DetailList.Root key={`${familyPlan.id}-age-banded-${premium.key}`}>
        <DetailList.Item
          label={startCase(premium.key)}
          value={<FormattedCurrency value={this.toCurrency(premium.value.total)} />}
          data-testid={`rate-age-banded-total-${familyPlan.id}-${premium.key}`}
        />

        {premiumValues.map((value, index) => (
          <DetailList.Item
            key={`${familyPlan.id}-${index}`}
            label={(
              <span className="inline-flex items-center gap-1">
                {this.memberTitle(index, familyPlan)}
                <IoMdArrowForward size={14} />
                {this.memberDetails(index, familyPlan)}
              </span>
            )}
            value={<FormattedCurrency value={this.toCurrency(value)} />}
            data-testid={`rate-age-banded-member-${familyPlan.id}-${index}`}
          />
        ))}
      </DetailList.Root>
    )
  }

  findMember = (index, familyPlan) => {
    const family = this.props.census.families.find((fam) => fam.name === familyPlan.member_external_id)

    return family.members[index]
  }

  memberTitle = (index, familyPlan) => {
    const familyMember = this.findMember(index, familyPlan)

    return <span>{`${startCase(familyMember.member_type)} `}</span>
  }

  memberDetails = (index, familyPlan) => {
    const familyMember = this.findMember(index, familyPlan)
    const gender = capitalize(familyMember.gender)
    const tobacco = familyMember.tobacco ? 'Yes' : 'No'

    return <em>{`Age ${familyMember.age}, ${gender}, Tobacco ${tobacco}`}</em>
  }

  formatKey = (key) => {
    switch (key) {
    case 'EE':
      return 'Employee'
    case 'ES':
      return 'Employee plus Spouse'
    case 'EC':
      return 'Employee plus Child'
    case 'FA':
      return 'Family'
    default:
      return key
    }
  }

  formatValue = (value) => {
    return !isUndefined(value) ? value : '–'
  }

  // This renders inside the card's .ideon-ui-scope, so it cannot use the
  // legacy howie_info classes -- the scope zeroes the border, padding, margin
  // and type those rules assume, and ADR 0011 forbids legacy markup inside it.
  statusComponent = (text) => (
    <div className="flex items-center gap-2 py-3 text-sm text-ideon-gray-70 dark:text-ideon-gray-60">
      <LoadingSpinner />
      <span>{text || 'Loading'}</span>
    </div>
  )

  quoteRate = () => {
    return this.props.quoteRates.find((rate) => rate.id === this.props.planPremiumsByFamily[0].rate_id)
  }

  levelFundedFields = () => {
    return (
      <div className={GROUP_CLASS}>
        <div className={HEADER_CLASS} data-testid="rate-header">Level Funded fields</div>

        <DetailList.Root>
          <DetailList.Item
            label="Stop Loss Amount"
            value={this.formatValue(this.quoteRate().stop_loss_amount)}
            data-testid="rate-stop-loss-amount"
          />
          <DetailList.Item
            label="Claims Surplus Fraction"
            value={this.formatValue(this.quoteRate().claims_surplus_fraction)}
            data-testid="rate-claims-surplus-fraction"
          />
          <DetailList.Item
            label="Monthly Claims Fund"
            value={this.formatValue(this.quoteRate().premiums['4_tier_composite'].claims_fund)}
            data-testid="rate-monthly-claims-fund"
          />
          <DetailList.Item
            label="Monthly Fixed Costs"
            value={this.formatValue(this.quoteRate().premiums['4_tier_composite'].fixed_costs)}
            data-testid="rate-monthly-fixed-costs"
          />
        </DetailList.Root>
      </div>
    )
  }

  render() {
    const { plan, planPremiumsByFamily, quoteRateMemberRates } = this.props

    if (planPremiumsByFamily[0].rate_id
      && ((!quoteRateMemberRates.created) || (quoteRateMemberRates.attributes.plan_id !== plan.id))) {
      return this.statusComponent('Loading rates')
    }

    if (planPremiumsByFamily[0].rate_id) {
      return (
        <div>
          {this.props.census.audience === 'Level Funded' && this.levelFundedFields()}
          {this.quoteRateMemberRatesComponents}
        </div>
      )
    }

    return (
      <div key={`${plan.id}-rates`}>
        {this.familyComponents}
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  const quoteRateMemberRates = state.quoteRateMemberRates.create
  return {
    quoteRateMemberRates,
    quoteRateMemberRatesCreated: quoteRateMemberRates.created,
    quoteRates: state.quoteRates.create.responseAttributes
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  quoteRateMemberRatesCreate: quoteRateMemberRatesActions.create.main,
  quoteRateMemberRatesReset: quoteRateMemberRatesActions.create.reset
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(PlanRatesComponent)
